import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, AlertCircle, Minus, Plus, RotateCcw, X } from "lucide-react";
import type { OrderWithCheckins } from "@/pages/producers/Scanner";

interface ScanResultProps {
  result: OrderWithCheckins | null;
  status: 'idle' | 'valid' | 'invalid' | 'exhausted';
  onCheckIn: (count: number) => void;
  onReset: () => void;
}

export function ScanResult({ result, status, onCheckIn, onReset }: ScanResultProps) {
  const remainingTickets = result ? result.total_tickets - result.checked_in_count : 0;
  const [checkInCount, setCheckInCount] = useState(Math.min(remainingTickets, 1));

  const statusConfig = {
    valid: {
      icon: CheckCircle2,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500',
      label: 'Valid Ticket',
    },
    invalid: {
      icon: XCircle,
      color: 'text-destructive',
      bgColor: 'bg-destructive/10',
      borderColor: 'border-destructive',
      label: 'Invalid Ticket',
    },
    exhausted: {
      icon: AlertCircle,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500',
      label: 'Fully Checked In',
    },
    idle: {
      icon: CheckCircle2,
      color: 'text-muted-foreground',
      bgColor: 'bg-muted',
      borderColor: 'border-border',
      label: '',
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  if (status === 'invalid') {
    return (
      <Card className={`${config.bgColor} border-2 ${config.borderColor}`}>
        <CardContent className="p-6 text-center">
          <Icon className={`h-16 w-16 mx-auto mb-4 ${config.color}`} />
          <h3 className={`text-xl font-bold mb-2 ${config.color}`}>
            {config.label}
          </h3>
          <p className="text-muted-foreground mb-4">
            This ticket reference was not found for this event or the order is not completed.
          </p>
          <Button onClick={onReset} variant="outline" className="w-full">
            <RotateCcw className="h-4 w-4 mr-2" />
            Scan Another Ticket
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!result) return null;

  return (
    <Card className={`${config.bgColor} border-2 ${config.borderColor} relative`}>
      {/* Close button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onReset}
        className="absolute top-2 right-2 h-8 w-8 rounded-full hover:bg-background/50"
      >
        <X className="h-4 w-4" />
      </Button>
      
      <CardContent className="p-6 pt-10">
        <div className="text-center mb-4">
          <Icon className={`h-12 w-12 mx-auto mb-3 ${config.color}`} />
          <Badge 
            variant="outline" 
            className={`${config.color} ${config.borderColor} mb-2`}
          >
            {config.label}
          </Badge>
          <h3 className="text-lg font-bold text-foreground">
            {result.user_first_name} {result.user_last_name}
          </h3>
          <p className="text-sm text-muted-foreground">{result.reference}</p>
        </div>

        {/* Ticket breakdown */}
        <div className="bg-background/50 rounded-lg p-3 mb-4 space-y-1 text-sm">
          {result.vip_ticket_quantity && result.vip_ticket_quantity > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">VIP Tickets</span>
              <span className="font-medium text-foreground">{result.vip_ticket_quantity}</span>
            </div>
          )}
          {result.early_bird_tickets && result.early_bird_tickets > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Early Bird</span>
              <span className="font-medium text-foreground">{result.early_bird_tickets}</span>
            </div>
          )}
          {result.standing_tickets && result.standing_tickets > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">General Admission</span>
              <span className="font-medium text-foreground">{result.standing_tickets}</span>
            </div>
          )}
          <div className="border-t border-border pt-1 mt-1 flex justify-between font-medium">
            <span className="text-muted-foreground">Total Tickets</span>
            <span className="text-foreground">{result.total_tickets}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Already Checked In</span>
            <span className={result.checked_in_count > 0 ? 'text-orange-500' : 'text-foreground'}>
              {result.checked_in_count}
            </span>
          </div>
          <div className="flex justify-between font-bold">
            <span className="text-muted-foreground">Remaining</span>
            <span className={config.color}>{remainingTickets}</span>
          </div>
        </div>

        {status === 'valid' && remainingTickets > 0 && (
          <>
            {/* Check-in count selector */}
            <div className="mb-4">
              <p className="text-sm text-muted-foreground text-center mb-2">
                How many people are checking in?
              </p>
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCheckInCount(Math.max(1, checkInCount - 1))}
                  disabled={checkInCount <= 1}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="text-3xl font-bold text-foreground w-12 text-center">
                  {checkInCount}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCheckInCount(Math.min(remainingTickets, checkInCount + 1))}
                  disabled={checkInCount >= remainingTickets}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <Button 
              onClick={() => onCheckIn(checkInCount)} 
              className="w-full bg-green-600 hover:bg-green-700"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Check In {checkInCount} {checkInCount === 1 ? 'Person' : 'People'}
            </Button>
          </>
        )}

        {status === 'exhausted' && (
          <div className="text-center">
            <p className="text-sm text-orange-500 mb-4">
              All tickets for this order have already been checked in.
            </p>
          </div>
        )}

        <Button 
          onClick={onReset} 
          variant="outline" 
          className="w-full mt-2"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Scan Another Ticket
        </Button>
      </CardContent>
    </Card>
  );
}
